import React, { useState, useEffect, useRef } from "react";
import QRCode from "react-qr-code";
import HiaFooter from "../../../widgets/HiaFooter";
import { Grid } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import DialogContent from "@material-ui/core/DialogContent";
import "./index.css";
import { env } from "../../../config/runtimeEnv";
import { SESSION_WAITING } from "../../../actions/authentication";

const POLLING_PERIOD_MS = 5000;
const SESSION_LIFETIME_MS = 300000; // the QR session expires after 5 minutes

const detectDeviceType = () =>
  /mobi|android|tablet|ipad|iphone/.test(navigator.userAgent.toLowerCase()) ? "mobile" : "desktop";

// Runtime-first (window.__ENV__ injected by the BFF), with REACT_APP_* build-time and a hard-coded
// default as fallbacks — see src/config/runtimeEnv.js. `SHOWROOM_SESSION_CLIENT_ID` is the QR
// session id prefix.
const newSessionId = () =>
  `${env("SHOWROOM_SESSION_CLIENT_ID", "showroom")}-${Math.random().toString(36).substring(2, 11)}`;

const qrUrl = (sessionId) =>
  `https://app.hiasecure.com/sessions/?sessionId=${sessionId}&serviceName=showroom`;

// QR login, pairing step. The mobile app pairs directly on back-tenant (public route); this
// component polls the session through the BFF until it is paired, fails, or expires — the polling
// always stops in those three cases and on unmount.
const Component = ({ actions }) => {
  const [deviceType] = useState(detectDeviceType);
  const [sessionId, setSessionId] = useState(newSessionId);
  const [waitingSession, setWaitingSession] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!waitingSession || sessionExpired) {
      return undefined;
    }
    let stopped = false;
    let interval = null;
    const stop = () => {
      stopped = true;
      clearInterval(interval);
    };
    const poll = () => {
      if (stopped || inFlight.current) return;
      inFlight.current = true;
      actions.getSessionDetails(sessionId).then((outcome) => {
        inFlight.current = false;
        if (outcome !== SESSION_WAITING) stop();
      });
    };
    interval = setInterval(poll, POLLING_PERIOD_MS);
    const expiry = setTimeout(() => {
      stop();
      setSessionExpired(true);
    }, SESSION_LIFETIME_MS);
    return () => {
      stop();
      clearTimeout(expiry);
    };
  }, [waitingSession, sessionExpired, sessionId, actions]);

  const signIn = () => {
    setWaitingSession(true);
    if (deviceType === "desktop") {
      setShowQrCode(true);
    } else {
      // Mobile/tablet: open the HIA app through its deeplink.
      window.open(qrUrl(sessionId), "_blank");
    }
  };

  const restart = () => {
    inFlight.current = false;
    setSessionId(newSessionId());
    setSessionExpired(false);
    setWaitingSession(false);
    setShowQrCode(false);
  };

  const qrCodeData = qrUrl(sessionId);

  return (
    <div id="mire">
      <div className="mireContainer">
        <div className="mireLogoBox">
          <img src="/showroom.png" alt="HIAsecure" className="mireLogo" />
        </div>
        <p className="mireTitle">Se connecter</p>
        <Grid item xs={12}>
          <DialogContent>
            <Grid container direction="column" alignItems="center" justify="center">
              {sessionExpired ? (
                <div className="loginRefusal" role="alert" data-kind="EXPIRED" style={{ textAlign: "center" }}>
                  <p className="loginRefusalMessage">La session a expiré. Recommencez la connexion.</p>
                  <Button type="button" color="primary" onClick={restart}>
                    Recommencer
                  </Button>
                </div>
              ) : deviceType === "mobile" ? (
                <>
                  <Button onClick={signIn} type="button" color="primary">
                    Sign in
                  </Button>
                  {showQrCode && (
                    <div style={{ textAlign: "center" }}>
                      <QRCode value={qrCodeData} size={window.innerWidth < 600 ? 200 : 300} />
                    </div>
                  )}
                  <p
                    onClick={() => setShowQrCode(!showQrCode)}
                    style={{ textAlign: "center", marginTop: "10px" }}
                  >
                    {showQrCode ? "Enlever le QR code" : "Afficher le QR code"}
                  </p>
                </>
              ) : !showQrCode ? (
                <Button onClick={signIn} type="button" color="primary">
                  Sign in
                </Button>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <QRCode value={qrCodeData} />
                </div>
              )}
            </Grid>
          </DialogContent>
        </Grid>
        <HiaFooter />
      </div>
    </div>
  );
};

export default Component;
